// REACT IMPORTS
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

// CSS
import "./TeamsMessenger.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// EDITOR
import "ckeditor5/ckeditor5.css";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
	ClassicEditor,
	AutoLink,
	Bold,
	Essentials,
	Italic,
	Link,
	List,
	Paragraph,
	Strikethrough,
} from "ckeditor5";
import DOMPurify from "dompurify";

// MUI COMPONENTS
import {
	Autocomplete,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import ReplayIcon from "@mui/icons-material/Replay";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

// HELPERS
import {
	FIRST_NAME_TOKEN,
	getTeamsToken,
	graphErrorText,
	personalize,
	sendTeamsMessage,
	userKeyOf,
} from "./teamsChat";

const TOOL_NAME = "Teams Messenger";
const SEVERITY_INFO_ID = 1; // automation_log_severity
const SEVERITY_ERROR_ID = 4;

const EDITOR_CONFIG = {
	plugins: [
		Essentials,
		Paragraph,
		Bold,
		Italic,
		Strikethrough,
		List,
		Link,
		AutoLink,
	],
	toolbar: [
		"bold",
		"italic",
		"strikethrough",
		"|",
		"bulletedList",
		"numberedList",
		"|",
		"link",
		"|",
		"undo",
		"redo",
	],
	link: { defaultProtocol: "https://" },
	initialData: `<p>Hi ${FIRST_NAME_TOKEN}! </p>`,
};

// Plain text of message HTML, for the automation log
const textOf = (html) =>
	new DOMParser().parseFromString(html, "text/html").body.textContent || "";

// Only spaces / empty paragraphs ("<p>&nbsp;</p>") counts as no message
const isBlank = (html) => !textOf(html).trim();

const fullName = (e) => `${e.first_name || ""} ${e.last_name || ""}`.trim();

const StatusIcon = ({ status }) => {
	if (status === "sending") return <CircularProgress size={16} />;
	if (status === "sent")
		return (
			<CheckCircleIcon fontSize="small" style={{ color: "#2e7d32" }} />
		);
	if (status === "failed")
		return <ErrorIcon fontSize="small" style={{ color: "#c62828" }} />;
	return null;
};

// REACT COMPONENT
const TeamsMessenger = () => {
	// MSAL
	const { instance, accounts } = useMsal();
	const senderKey = accounts[0]?.localAccountId; // Azure AD object id

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATE
	const [employees, setEmployees] = useState([]);
	const [rolesById, setRolesById] = useState(new Map());
	const [loadError, setLoadError] = useState("");
	const [recipients, setRecipients] = useState([]);
	const [office, setOffice] = useState("");
	const [role, setRole] = useState("");
	const [isEmpty, setIsEmpty] = useState(true);
	const [confirmTargets, setConfirmTargets] = useState(null); // { list, isRetry }
	const [isSending, setIsSending] = useState(false);
	const [results, setResults] = useState({}); // employee id → { status, error }
	const [sendError, setSendError] = useState("");

	// EDITOR
	const editorRef = useRef(null);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: TOOL_NAME,
			employeeID: employeeID,
			notes: "Accessed the Teams Messenger page",
		}).catch(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const load = async () => {
			try {
				const [emp, roles] = await Promise.all([
					getAPICall(
						instance,
						accounts[0],
						"/api/employees/all/active",
					),
					getAPICall(
						instance,
						accounts[0],
						"/api/permissions/employees/all",
					),
				]);
				setEmployees(emp.data || []);
				// employee id → role names
				setRolesById(
					new Map(
						(roles.data || []).map((r) => [
							r.id,
							r.split_roles ? r.split_roles.split("_SPLIT_") : [],
						]),
					),
				);
				setLoadError("");
			} catch (err) {
				console.error(err);
				setLoadError("Couldn't load employees.");
			}
		};
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// DERIVED
	// The sender can't open a 1:1 chat with themselves
	const isSelf = (e) =>
		Number(e.id) === Number(employeeID) ||
		(senderKey && userKeyOf(e)?.toLowerCase() === senderKey.toLowerCase());

	const offices = useMemo(
		() =>
			[...new Set(employees.map((e) => e.office).filter(Boolean))].sort(),
		[employees],
	);

	// Roles held by at least one active employee
	const usedRoles = useMemo(
		() =>
			[
				...new Set(employees.flatMap((e) => rolesById.get(e.id) || [])),
			].sort(),
		[employees, rolesById],
	);

	const sendable = recipients.filter((e) => userKeyOf(e) && !isSelf(e));
	const skipped = recipients.length - sendable.length;
	const sentCount = Object.values(results).filter(
		(r) => r.status === "sent",
	).length;
	const failedCount = Object.values(results).filter(
		(r) => r.status === "failed",
	).length;

	// HANDLERS
	const addRecipients = (list) => {
		setRecipients((prev) => {
			const have = new Set(prev.map((e) => e.id));
			const added = list.filter((e) => !have.has(e.id));
			return [...prev, ...added].sort((a, b) =>
				fullName(a).localeCompare(fullName(b)),
			);
		});
		setResults({});
	};

	const removeRecipient = (id) => {
		setRecipients((prev) => prev.filter((e) => e.id !== id));
		setResults((prev) => {
			const next = { ...prev };
			delete next[id];
			return next;
		});
	};

	const handleAddOffice = (value) => {
		setOffice("");
		if (value) addRecipients(employees.filter((e) => e.office === value));
	};

	const handleAddRole = (value) => {
		setRole("");
		if (value)
			addRecipients(
				employees.filter((e) =>
					(rolesById.get(e.id) || []).includes(value),
				),
			);
	};

	const insertFirstName = () => {
		const editor = editorRef.current;
		if (!editor) return;
		editor.model.change((writer) =>
			editor.model.insertContent(writer.createText(FIRST_NAME_TOKEN)),
		);
		editor.editing.view.focus();
	};

	const messageHtml = () =>
		DOMPurify.sanitize(editorRef.current?.getData() || "");

	// One automation_log row; success = 0 turns the tool red on the Automation
	// Logs Dashboard
	const logRun = (success, severityId, lines) =>
		postAPICall(instance, accounts[0], "/api/logs/automation/add", {
			employeeID: employeeID,
			message: lines.join("\n").slice(0, 4095),
			success: success ? 1 : 0,
			tool_name: TOOL_NAME,
			automation_log_severity_id: severityId,
		}).catch(() => {});

	// Sends to the confirmed targets: everyone sendable, or on a retry just the
	// ones that failed (keeping everyone else's results)
	const handleSend = async () => {
		// The dialog's Send stays clickable while it fades out
		if (!confirmTargets || isSending) return;
		const { list: targets, isRetry } = confirmTargets;
		setConfirmTargets(null);
		setSendError("");
		setIsSending(true);
		const sending = Object.fromEntries(
			targets.map((e) => [e.id, { status: "sending" }]),
		);
		setResults((prev) => (isRetry ? { ...prev, ...sending } : sending));

		const messageText = `Message: ${textOf(messageHtml())}`;
		const names = (list) => list.map(fullName).join(", ");

		// Logged up front so a send cut short (tab closed) still has a record
		logRun(true, SEVERITY_INFO_ID, [
			`${isRetry ? "Retrying failed sends" : "Started sending a Teams message"} to ${targets.length}: ${names(targets)}`,
			messageText,
		]);

		let token;
		try {
			token = await getTeamsToken(instance, accounts[0]);
		} catch (err) {
			console.error(err);
			const why = graphErrorText(err);
			setSendError(
				`Microsoft didn't grant permission to send Teams messages: ${why}`,
			);
			logRun(false, SEVERITY_ERROR_ID, [
				`Couldn't send: Microsoft didn't grant permission to send Teams messages (${why})`,
				messageText,
			]);
			// A retry's targets stay failed; a first send clears
			setResults((prev) =>
				isRetry
					? {
							...prev,
							...Object.fromEntries(
								targets.map((e) => [
									e.id,
									{ status: "failed", error: why },
								]),
							),
						}
					: {},
			);
			setIsSending(false);
			return;
		}

		const html = messageHtml();
		const outcome = {};

		// One at a time, to stay under Graph's chat throttling limits
		for (const e of targets) {
			try {
				await sendTeamsMessage(
					token,
					senderKey,
					userKeyOf(e),
					personalize(html, e),
				);
				outcome[e.id] = { status: "sent" };
			} catch (err) {
				console.error(err);
				outcome[e.id] = {
					status: "failed",
					error: graphErrorText(err),
				};
				logRun(false, SEVERITY_ERROR_ID, [
					`Failed to send a Teams message to ${fullName(e)}: ${outcome[e.id].error}`,
				]);
			}
			setResults((prev) => ({ ...prev, [e.id]: outcome[e.id] }));
		}

		const sent = targets.filter((e) => outcome[e.id].status === "sent");
		const failed = targets.filter((e) => outcome[e.id].status === "failed");
		logRun(
			failed.length === 0,
			failed.length ? SEVERITY_ERROR_ID : SEVERITY_INFO_ID,
			[
				`Finished${isRetry ? " retrying" : ""}: sent a Teams message to ${sent.length} of ${targets.length}`,
				...(sent.length ? [`Sent: ${names(sent)}`] : []),
				...failed.map(
					(e) => `Failed: ${fullName(e)} (${outcome[e.id].error})`,
				),
				messageText,
			],
		);

		setIsSending(false);
	};

	const handleConfirm = () => {
		if (isEmpty) return setSendError("Write a message first.");
		if (!sendable.length)
			return setSendError("Add at least one recipient.");
		setSendError("");
		setConfirmTargets({ list: sendable, isRetry: false });
	};

	// Only the people whose send failed
	const failedRecipients = sendable.filter(
		(e) => results[e.id]?.status === "failed",
	);

	const handleRetryFailed = () => {
		if (isEmpty) return setSendError("Write a message first.");
		setSendError("");
		setConfirmTargets({ list: failedRecipients, isRetry: true });
	};

	const targets = confirmTargets?.list || [];

	// RENDER
	return (
		<div className="TeamsMessenger">
			<div className="teamsMessengerHeader">
				<div className="teamsMessengerHeaderTitle">Teams Messenger</div>
				<div className="teamsMessengerHeaderNote">
					Each person gets a 1:1 Teams chat from you
				</div>
			</div>

			{loadError && (
				<div className="teamsMessengerError">{loadError}</div>
			)}

			<div className="teamsMessengerBody">
				{/* MESSAGE */}
				<div className="teamsMessengerPanel teamsMessengerMessagePanel">
					<div className="teamsMessengerPanelTitle">
						Message
						<Button
							size="small"
							variant="outlined"
							onClick={insertFirstName}
							disabled={isSending}
						>
							Insert First Name
						</Button>
					</div>
					<div className="teamsMessengerEditor">
						<CKEditor
							editor={ClassicEditor}
							config={EDITOR_CONFIG}
							disabled={isSending}
							onReady={(editor) => {
								editorRef.current = editor;
								setIsEmpty(isBlank(editor.getData()));
							}}
							onChange={(_, editor) =>
								setIsEmpty(isBlank(editor.getData()))
							}
						/>
					</div>
					<div className="teamsMessengerHint">
						{FIRST_NAME_TOKEN} is replaced with each person's first
						name.
					</div>
				</div>

				{/* RECIPIENTS */}
				<div className="teamsMessengerPanel teamsMessengerRecipientPanel">
					<div className="teamsMessengerPanelTitle">
						Recipients ({recipients.length})
						{recipients.length > 0 && (
							<Button
								size="small"
								onClick={() => {
									setRecipients([]);
									setResults({});
								}}
								disabled={isSending}
							>
								Clear
							</Button>
						)}
					</div>

					<Autocomplete
						multiple
						size="small"
						disableCloseOnSelect
						options={employees}
						value={employees.filter((e) =>
							recipients.some((r) => r.id === e.id),
						)}
						onChange={(_, value) => {
							setRecipients(
								[...value].sort((a, b) =>
									fullName(a).localeCompare(fullName(b)),
								),
							);
							setResults({});
						}}
						getOptionLabel={fullName}
						isOptionEqualToValue={(a, b) => a.id === b.id}
						renderTags={() => null}
						renderOption={(props, option, { selected }) => (
							<li {...props} key={option.id}>
								<Checkbox
									size="small"
									icon={
										<CheckBoxOutlineBlankIcon fontSize="small" />
									}
									checkedIcon={
										<CheckBoxIcon fontSize="small" />
									}
									checked={selected}
									style={{ marginRight: 6 }}
								/>
								{fullName(option)}
								{option.office && (
									<span className="teamsMessengerOptionOffice">
										{option.office}
									</span>
								)}
							</li>
						)}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder="Search employees to add"
							/>
						)}
						disabled={isSending}
					/>

					<div className="teamsMessengerQuickAdd">
						<Button
							size="small"
							variant="outlined"
							startIcon={<PersonAddIcon />}
							onClick={() => addRecipients(employees)}
							disabled={isSending || !employees.length}
						>
							All Active
						</Button>
						<TextField
							select
							size="small"
							label="Add office"
							value={office}
							onChange={(e) => handleAddOffice(e.target.value)}
							disabled={isSending}
							className="teamsMessengerQuickSelect"
						>
							{offices.map((o) => (
								<MenuItem key={o} value={o}>
									{o}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							size="small"
							label="Add role"
							value={role}
							onChange={(e) => handleAddRole(e.target.value)}
							disabled={isSending}
							className="teamsMessengerQuickSelect"
						>
							{usedRoles.map((r) => (
								<MenuItem key={r} value={r}>
									{r}
								</MenuItem>
							))}
						</TextField>
					</div>

					<div className="teamsMessengerRecipientList">
						{recipients.length === 0 && (
							<div className="teamsMessengerEmpty">
								No one added yet.
							</div>
						)}
						{recipients.map((e) => {
							const result = results[e.id];
							const warning = isSelf(e)
								? "That's you, so you'll be skipped"
								: !userKeyOf(e)
									? "No Teams ID or email, so they'll be skipped"
									: null;
							return (
								<div
									key={e.id}
									className="teamsMessengerRecipient"
								>
									<span className="teamsMessengerRecipientName">
										{fullName(e)}
									</span>
									{e.office && (
										<span className="teamsMessengerOptionOffice">
											{e.office}
										</span>
									)}
									<span className="teamsMessengerRecipientStatus">
										{warning && (
											<Tooltip
												title={warning}
												arrow
												placement="top"
											>
												<WarningIcon
													fontSize="small"
													style={{ color: "#ca8a04" }}
												/>
											</Tooltip>
										)}
										{result?.error ? (
											<Tooltip
												title={result.error}
												arrow
												placement="top"
											>
												<span>
													<StatusIcon
														status={result.status}
													/>
												</span>
											</Tooltip>
										) : (
											<StatusIcon
												status={result?.status}
											/>
										)}
									</span>
									<Tooltip
										title="Remove"
										arrow
										placement="top"
									>
										<span>
											<IconButton
												size="small"
												onClick={() =>
													removeRecipient(e.id)
												}
												disabled={isSending}
											>
												<CloseIcon fontSize="small" />
											</IconButton>
										</span>
									</Tooltip>
								</div>
							);
						})}
					</div>
				</div>
			</div>

			<div className="teamsMessengerFooter">
				{sendError && (
					<span className="teamsMessengerError">{sendError}</span>
				)}
				{!sendError &&
					(sentCount > 0 || failedCount > 0) &&
					!isSending && (
						<span className="teamsMessengerSummary">
							Sent {sentCount}
							{failedCount > 0 &&
								` · ${failedCount} failed (hover the red icons for why)`}
						</span>
					)}
				{failedRecipients.length > 0 && !isSending && (
					<Button
						variant="outlined"
						startIcon={<ReplayIcon />}
						onClick={handleRetryFailed}
					>
						Retry Failed ({failedRecipients.length})
					</Button>
				)}
				<Button
					variant="contained"
					disableElevation
					startIcon={
						isSending ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							<SendIcon />
						)
					}
					onClick={handleConfirm}
					disabled={isSending}
				>
					{isSending ? "Sending…" : `Send to ${sendable.length}`}
				</Button>
			</div>

			<Dialog
				open={Boolean(confirmTargets)}
				onClose={() => setConfirmTargets(null)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle
					style={{ backgroundColor: "#184C93", color: "white" }}
				>
					{confirmTargets?.isRetry
						? "Retry Failed Sends"
						: "Send Teams Message"}
				</DialogTitle>
				<DialogContent className="teamsMessengerDialogContent">
					<div>
						This sends a 1:1 Teams chat from you to{" "}
						<strong>{targets.length}</strong>{" "}
						{targets.length === 1 ? "person" : "people"}
						{confirmTargets?.isRetry
							? " whose send failed. Everyone else already has it."
							: "."}
						{!confirmTargets?.isRetry &&
							skipped > 0 &&
							` ${skipped} will be skipped.`}{" "}
						It can't be unsent.
					</div>
					<div className="teamsMessengerPreviewLabel">
						Preview for {targets[0] ? fullName(targets[0]) : ""}
					</div>
					<div
						className="teamsMessengerPreview"
						dangerouslySetInnerHTML={{
							__html: confirmTargets
								? personalize(messageHtml(), targets[0])
								: "",
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						variant="outlined"
						onClick={() => setConfirmTargets(null)}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						disableElevation
						onClick={handleSend}
					>
						Send
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default TeamsMessenger;
